import { useMemo, useState } from 'react';
import { Plus, Pencil, Package, Trash2, ImageIcon, Upload, Tag, Boxes, RotateCcw, XOctagon } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProductExportMenu } from '@/components/inventory/ProductExportMenu';
import { Chip } from '@/components/ui/Chip';
import { SearchInput } from '@/components/ui/SearchInput';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { ProductForm } from '@/components/inventory/ProductForm';
import { CsvImportDialog } from '@/components/inventory/CsvImportDialog';
import { BulkImageUploadDialog } from '@/components/inventory/BulkImageUploadDialog';
import { ProductImage } from '@/components/inventory/ProductImage';
import { CategoryManager } from '@/components/inventory/CategoryManager';
import {
  useProducts,
  useDeleteProduct,
  useUpdateProduct,
  useHardDeleteProduct,
  useSetProductsActive,
  useHardDeleteUnusedProducts,
} from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { categoryName } from '@/lib/api/productsExport';
import { useRowSelection } from '@/hooks/useRowSelection';
import {
  BulkActionBar,
  RowCheckbox,
  SelectAllCheckbox,
  describeBulkDelete,
} from '@/components/ui/BulkSelect';
import { getProductUsage, isProductUsed, describeProductUsage } from '@/lib/api/products';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { formatIDR, formatNumber, cn } from '@/lib/format';
import type { Product } from '@/types/domain';

type Tab = 'products' | 'categories';

export default function Inventory() {
  const { user } = useAuth();
  const { data: products, isLoading } = useProducts();
  // Every category, retired ones too: a product can still point at a category
  // that was deactivated after it was filed there.
  const { data: categories } = useCategories();
  const categoryById = useMemo(
    () => new Map((categories ?? []).map((c) => [c.id, c.name])),
    [categories],
  );
  const deleteProduct = useDeleteProduct();
  const updateProduct = useUpdateProduct();
  const hardDelete = useHardDeleteProduct();
  const setProductsActive = useSetProductsActive();
  const hardDeleteUnused = useHardDeleteUnusedProducts();
  const { can, scopeLimited } = usePermissions();
  const canManage = can('inventory', 'edit');
  // Permanent delete has its own permission, and inventory.delete -- not
  // inventory.edit -- is what the database checks. It is also withheld from
  // scope-limited roles: whether a product was ever used is counted through the
  // caller's RLS, and a rayon-limited view would call a product sold elsewhere
  // unused.
  const canDelete = can('inventory', 'delete') && !scopeLimited;
  const canSelect = canManage || canDelete;

  const [tab, setTab] = useState<Tab>('products');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [imagesOpen, setImagesOpen] = useState(false);

  const [importOpen, setImportOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [hardTarget, setHardTarget] = useState<Product | null>(null);
  const [bulkMode, setBulkMode] = useState<'deactivate' | 'activate' | 'delete' | null>(null);

  const filtered = useMemo(() => {
    const list = products ?? [];
    return list
      .filter((p) => {
        if (activeFilter === 'active' && !p.isActive) return false;
        if (activeFilter === 'inactive' && p.isActive) return false;
        if (search) {
          const s = search.toLowerCase();
          return (
            p.name.toLowerCase().includes(s) ||
            p.sku.toLowerCase().includes(s) ||
            (p.brand?.toLowerCase().includes(s) ?? false) ||
            (p.description?.toLowerCase().includes(s) ?? false)
          );
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, search, activeFilter]);

  // Memoised, or useRowSelection rebuilds its lookup on every render.
  const visibleIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const selection = useRowSelection(visibleIds);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !user) return;
    const reason = window.prompt(
      `Deactivate "${deleteTarget.name}"? Alasan (opsional, untuk audit):`,
    );
    if (reason === null) return; // user cancelled
    await deleteProduct.mutateAsync({
      productId: deleteTarget.id,
      deletedBy: user.uid,
      deletedByName: user.displayName ?? null,
      deletedByEmail: user.email ?? null,
      reason: reason || undefined,
    });
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const handleReactivate = async (p: Product) => {
    if (!user) return;
    try {
      await updateProduct.mutateAsync({
        id: p.id,
        input: {
          isActive: true,
          updatedBy: user.uid,
          updatedByName: user.displayName ?? null,
          updatedByEmail: user.email ?? null,
        },
      });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Gagal mengaktifkan produk');
    }
  };

  // Permanent delete is only offered for a product nothing references. The
  // usage check happens before the dialog opens so the owner sees *why* it is
  // refused (counts), not just a failed confirm.
  const askHardDelete = async (p: Product) => {
    try {
      const usage = await getProductUsage(p.id);
      if (isProductUsed(usage)) {
        window.alert(
          `"${p.name}" sudah pernah dipakai (${describeProductUsage(usage)}), jadi tidak bisa dihapus permanen. Nonaktifkan saja.`,
        );
        return;
      }
      setHardTarget(p);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Gagal memeriksa pemakaian produk');
    }
  };

  const handleHardDelete = async () => {
    if (!hardTarget) return;
    try {
      await hardDelete.mutateAsync(hardTarget.id);
      setHardTarget(null);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Gagal menghapus produk');
    }
  };

  // Bulk actions for the ticked rows. Activate and deactivate apply to every one
  // of them. Permanent delete removes only what nothing references and leaves
  // the rest ticked, so deactivating those is one more click.
  const handleBulk = async () => {
    if (!bulkMode) return;
    try {
      if (bulkMode === 'delete') {
        const result = await hardDeleteUnused.mutateAsync(selection.selectedIds);
        selection.keepOnly(result.inUse);
        window.alert(
          describeBulkDelete(result, 'produk', 'event, promo, atau transaksi', canManage),
        );
      } else {
        await setProductsActive.mutateAsync({
          ids: selection.selectedIds,
          isActive: bulkMode === 'activate',
        });
        selection.clear();
      }
    } catch (e) {
      // The message carries how far it got, so the ticks stay put for a retry.
      window.alert(
        e instanceof Error
          ? e.message
          : bulkMode === 'delete'
            ? 'Gagal menghapus produk'
            : 'Gagal memperbarui produk',
      );
    } finally {
      setBulkMode(null);
    }
  };

  const bulkHiddenNote =
    selection.hiddenCount > 0
      ? ` ${selection.hiddenCount} di antaranya sedang tidak terlihat karena filter.`
      : '';

  const bulkCopy = {
    activate: {
      title: 'Aktifkan produk terpilih?',
      body: `${selection.count} produk akan tampil lagi di katalog dan bisa dijual.`,
      confirm: 'Aktifkan',
    },
    deactivate: {
      title: 'Nonaktifkan produk terpilih?',
      body: `${selection.count} produk akan hilang dari katalog dan dari kasir. Transaksi lama tetap tersimpan dan produk bisa diaktifkan lagi kapan saja.`,
      confirm: 'Nonaktifkan',
    },
    delete: {
      title: 'Hapus permanen produk terpilih?',
      body: `${selection.count} produk akan diperiksa satu per satu. Yang belum pernah dipakai di event, promo, atau transaksi dihapus selamanya beserta gambarnya, dan tidak bisa dikembalikan. Yang sudah pernah dipakai tidak disentuh dan tetap tercentang.`,
      confirm: 'Hapus permanen',
    },
  };
  const bulkText = bulkMode ? bulkCopy[bulkMode] : null;

  const activeCount = (products ?? []).filter((p) => p.isActive).length;

  return (
    <>
      <PageHeader
        title="Inventory"
        description="Katalog produk. Stock di-track per-event, bukan global — atur alokasi dari halaman Event."
        actions={
          tab === 'products' && (
            <div className="flex items-center gap-2">
              {/* Export sits outside the owner guard: RLS lets any staff member
                  read products, and an export is a read. A cashier who needs
                  the price list should not have to ask an owner for it. */}
              <ProductExportMenu
                products={products ?? []}
                filters={{ status: activeFilter, search }}
                disabled={!products || products.length === 0}
              />
              {canManage && (
                <Button
                  variant="secondary"
                  leftIcon={<Upload size={16} />}
                  onClick={() => setImportOpen(true)}
                >
                  Import
                </Button>
              )}
              {canManage && (
                <Button
                  variant="secondary"
                  leftIcon={<ImageIcon size={16} />}
                  onClick={() => setImagesOpen(true)}
                  disabled={!products || products.length === 0}
                  title="Upload banyak gambar produk sekaligus"
                >
                  Upload gambar
                </Button>
              )}
              {canManage && (
                <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openCreate}>
                  New product
                </Button>
              )}
            </div>
          )
        }
      />

      {/* Tabs */}
      <div
        data-tour="inventory-filter"
        role="tablist"
        aria-label="Inventory sections"
        className="mb-6 inline-flex p-1 rounded bg-canvas border border-line"
      >
        <TabButton
          active={tab === 'products'}
          onClick={() => setTab('products')}
          icon={<Boxes size={14} />}
          label="Produk"
        />
        <TabButton
          active={tab === 'categories'}
          onClick={() => setTab('categories')}
          icon={<Tag size={14} />}
          label="Kategori"
        />
      </div>

      {tab === 'products' ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            <Card padding="md">
              <p className="text-overline uppercase text-ink-subtle font-medium">Total produk</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink">
                {formatNumber(products?.length ?? 0)}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-overline uppercase text-ink-subtle font-medium">Aktif</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink">
                {formatNumber(activeCount)}
              </p>
            </Card>
            <Card padding="md">
              <p className="text-overline uppercase text-ink-subtle font-medium">Nonaktif</p>
              <p className="text-metric font-display font-semibold mt-2 text-ink-subtle">
                {formatNumber((products?.length ?? 0) - activeCount)}
              </p>
            </Card>
          </div>

          <Card data-tour="inventory-table" padding="none">
            <div className="p-4 border-b border-line flex flex-col sm:flex-row gap-3">
              <div className="flex-1 min-w-0">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name, SKU, brand, or description…"
                />
              </div>
              <Select
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as typeof activeFilter)}
                className="sm:w-40"
                aria-label="Filter by status"
              >
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
                <option value="all">All</option>
              </Select>
            </div>

            {canSelect && (
              <BulkActionBar
                count={selection.count}
                hiddenCount={selection.hiddenCount}
                noun="produk"
                onClear={selection.clear}
              >
                {canManage && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<RotateCcw size={14} />}
                      onClick={() => setBulkMode('activate')}
                    >
                      Aktifkan
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Trash2 size={14} />}
                      onClick={() => setBulkMode('deactivate')}
                    >
                      Nonaktifkan
                    </Button>
                  </>
                )}
                {/* The only red button in the bar, because it is the only one
                    that cannot be undone. */}
                {canDelete && (
                  <Button
                    variant="danger"
                    size="sm"
                    leftIcon={<XOctagon size={14} />}
                    onClick={() => setBulkMode('delete')}
                  >
                    Hapus permanen
                  </Button>
                )}
              </BulkActionBar>
            )}

            {isLoading ? (
              <div className="py-16 text-center text-ink-subtle text-body">Loading products…</div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<Package size={24} strokeWidth={1.5} />}
                title={
                  search
                    ? 'Tidak ada produk yang cocok'
                    : activeFilter === 'inactive'
                      ? 'Tidak ada produk nonaktif'
                      : 'Belum ada produk'
                }
                description={
                  search
                    ? 'Coba kata kunci lain atau ubah filter.'
                    : 'Tambah produk pertama kamu ke katalog. Stock di-track per event.'
                }
                action={
                  !search && canManage && (
                    <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openCreate}>
                      Add product
                    </Button>
                  )
                }
              />
            ) : (
              <Table>
                <THead>
                  <TR>
                    {canSelect && (
                      <TH className="w-10">
                        <SelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </TH>
                    )}
                    {/* In the order of the owner's catalogue sheet, so the list
                        and the file being imported line up column for column. */}
                    <TH>Sub Kategori</TH>
                    <TH>Brand</TH>
                    <TH>SKU</TH>
                    <TH>Product Name</TH>
                    <TH>Unit</TH>
                    <TH className="text-right">Price</TH>
                    <TH>ImageUrl</TH>
                    <TH>Status</TH>
                    <TH className="text-right">Aksi</TH>
                  </TR>
                </THead>
                <TBody>
                  {filtered.map((p) => (
                    <TR key={p.id} className={cn(selection.isSelected(p.id) && 'bg-primary/5')}>
                      {canSelect && (
                        <TD className="w-10">
                          <RowCheckbox
                            checked={selection.isSelected(p.id)}
                            onChange={() => selection.toggle(p.id)}
                            label={`Pilih ${p.name}`}
                          />
                        </TD>
                      )}
                      <TD className="text-ink-subtle">{categoryName(p, categoryById) || '—'}</TD>
                      <TD className="text-ink-subtle">{p.brand ?? '—'}</TD>
                      <TD>
                        <code className="text-caption font-mono text-ink-subtle">{p.sku}</code>
                      </TD>
                      <TD>
                        <div className="min-w-0">
                          <p className="font-medium text-ink truncate">{p.name}</p>
                          {p.description && (
                            <p className="text-caption text-ink-subtle truncate max-w-xs">
                              {p.description}
                            </p>
                          )}
                        </div>
                      </TD>
                      <TD className="text-ink-subtle">{p.unit ?? 'pcs'}</TD>
                      <TD className="text-right font-medium">{formatIDR(p.price)}</TD>
                      <TD>
                        {/* The picture rather than its address: a column of URLs
                            is unreadable, and the image is what it is for. */}
                        <ProductImage
                          value={p.imageUrl}
                          alt={p.name}
                          className="w-10 h-10 rounded object-cover border border-line"
                          fallback={
                            <div className="w-10 h-10 rounded bg-line/30 flex items-center justify-center text-neutral">
                              <ImageIcon size={16} />
                            </div>
                          }
                        />
                      </TD>
                      <TD>
                        <Chip variant={p.isActive ? 'success' : 'default'}>
                          {p.isActive ? 'Active' : 'Inactive'}
                        </Chip>
                      </TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          {canManage && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(p)}
                                title="Edit"
                                aria-label="Edit"
                              >
                                <Pencil size={16} />
                              </Button>
                              {p.isActive ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setDeleteTarget(p);
                                    setDeleteOpen(true);
                                  }}
                                  title="Nonaktifkan (deactivate)"
                                  aria-label={`Nonaktifkan ${p.name}`}
                                >
                                  <Trash2 size={16} className="text-danger" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReactivate(p)}
                                  loading={updateProduct.isPending && updateProduct.variables?.id === p.id}
                                  title="Aktifkan kembali"
                                  aria-label={`Aktifkan kembali ${p.name}`}
                                >
                                  <RotateCcw size={16} className="text-success" />
                                </Button>
                              )}
                            </>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => askHardDelete(p)}
                              title="Hapus permanen (hanya jika belum pernah dipakai)"
                              aria-label={`Hapus permanen ${p.name}`}
                            >
                              <XOctagon size={16} className="text-ink-subtle" />
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        </>
      ) : (
        // Categories tab
        canManage ? (
          <CategoryManager />
        ) : (
          <Card>
            <p className="text-body text-ink-subtle text-center py-12">
              Hanya owner yang bisa mengelola kategori.
            </p>
          </Card>
        )
      )}

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} product={editing} />
      <BulkImageUploadDialog
        open={imagesOpen}
        onClose={() => setImagesOpen(false)}
        products={products ?? []}
      />
      <CsvImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Deactivate product?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" akan dinonaktifkan. Transaksi lama tetap tersimpan dan produk bisa diaktifkan lagi nanti.`
            : ''
        }
        confirmText="Deactivate"
        variant="danger"
        loading={deleteProduct.isPending}
      />

      <ConfirmDialog
        open={!!hardTarget}
        onClose={() => setHardTarget(null)}
        onConfirm={handleHardDelete}
        title="Hapus permanen?"
        description={
          hardTarget
            ? `"${hardTarget.name}" (SKU ${hardTarget.sku}) belum pernah dipakai di event, transaksi, atau retur. Produk akan dihapus dari database dan SKU-nya bisa dipakai lagi. Tidak bisa dibatalkan.`
            : ''
        }
        confirmText="Hapus permanen"
        variant="danger"
        loading={hardDelete.isPending}
      />

      <ConfirmDialog
        open={bulkText !== null}
        onClose={() => setBulkMode(null)}
        onConfirm={handleBulk}
        title={bulkText?.title ?? ''}
        description={bulkText ? bulkText.body + bulkHiddenNote : ''}
        confirmText={bulkText?.confirm}
        variant={bulkMode === 'activate' ? 'primary' : 'danger'}
        loading={setProductsActive.isPending || hardDeleteUnused.isPending}
      />
    </>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-small font-medium transition-colors',
        active ? 'bg-surface text-ink shadow-hover' : 'text-ink-subtle hover:text-ink',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
